import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Calendar, Clock, Users, Plus, Trash2, Check, X, 
  RefreshCw, Wand2, AlertCircle, CheckCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminAppointmentSlots() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newSlot, setNewSlot] = useState({
    date: "",
    time: "12:00",
    capacity_total: 6,
    active: true
  });

  const fetchSlots = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/appointment-slots`, {
        params: { include_inactive: showInactive },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(response.data.slots);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Fehler beim Laden der Termine");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, [showInactive]);

  const handleCreateSlot = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/appointment-slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Termin erstellt");
      setIsAddModalOpen(false);
      setNewSlot({ date: "", time: "12:00", capacity_total: 6, active: true });
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleToggleActive = async (slot) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/appointment-slots/${slot.id}`, 
        { active: !slot.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(slot.active ? "Deaktiviert" : "Aktiviert");
      fetchSlots();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!confirm("Diesen Termin-Slot wirklich löschen?")) return;
    
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/appointment-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Gelöscht");
      fetchSlots();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleGenerateWeek = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/appointment-slots/generate-week`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.slots_created} Slots erstellt`);
      fetchSlots();
    } catch (error) {
      toast.error("Fehler beim Generieren");
    }
  };

  // Group slots by date
  const groupedSlots = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="appointment-slots-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Termin-Slots</h1>
          <p className="text-slate-500 text-sm mt-1">
            Verwalte verfügbare Termine für Bewerber
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleGenerateWeek}
            className="gap-2"
          >
            <Wand2 className="w-4 h-4" />
            Woche generieren
          </Button>
          <Button 
            onClick={() => setIsAddModalOpen(true)}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Neuer Slot
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
        <div className="flex items-center gap-2">
          <Switch 
            checked={showInactive} 
            onCheckedChange={setShowInactive}
            id="show-inactive"
          />
          <Label htmlFor="show-inactive" className="text-sm text-slate-600">
            Inaktive anzeigen
          </Label>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchSlots}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Slots List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : Object.keys(groupedSlots).length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Keine Termine vorhanden</p>
          <Button 
            onClick={handleGenerateWeek} 
            className="mt-4 gap-2"
          >
            <Wand2 className="w-4 h-4" />
            Termine für diese Woche generieren
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedSlots).sort().map(([date, daySlots]) => (
            <div key={date} className="bg-white rounded-lg border overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b">
                <h3 className="font-semibold text-slate-900">
                  {formatDate(date)}
                </h3>
              </div>
              <div className="divide-y">
                {daySlots.sort((a, b) => a.time.localeCompare(b.time)).map((slot) => (
                  <div 
                    key={slot.id} 
                    className={`flex items-center justify-between p-4 ${!slot.active ? 'bg-slate-50 opacity-60' : ''}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-slate-700">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">{slot.time} Uhr</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Users className="w-4 h-4" />
                        <span className={slot.capacity_booked >= slot.capacity_total ? 'text-red-500' : ''}>
                          {slot.capacity_booked} / {slot.capacity_total} gebucht
                        </span>
                      </div>
                      {slot.capacity_booked >= slot.capacity_total && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          Ausgebucht
                        </span>
                      )}
                      {!slot.active && (
                        <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded-full">
                          Inaktiv
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(slot)}
                        className={slot.active ? "text-amber-600" : "text-green-600"}
                      >
                        {slot.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSlot(slot.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Slot Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Neuen Termin-Slot erstellen</DialogTitle>
            <DialogDescription>
              Termine können nur zwischen 11:00 und 15:00 Uhr liegen.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="date">Datum</Label>
              <Input
                id="date"
                type="date"
                value={newSlot.date}
                onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time">Uhrzeit</Label>
              <select
                id="time"
                value={newSlot.time}
                onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                className="w-full border rounded-md p-2"
              >
                <option value="11:00">11:00 Uhr</option>
                <option value="11:30">11:30 Uhr</option>
                <option value="12:00">12:00 Uhr</option>
                <option value="12:30">12:30 Uhr</option>
                <option value="13:00">13:00 Uhr</option>
                <option value="13:30">13:30 Uhr</option>
                <option value="14:00">14:00 Uhr</option>
                <option value="14:30">14:30 Uhr</option>
                <option value="15:00">15:00 Uhr</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="capacity">Kapazität</Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                max="20"
                value={newSlot.capacity_total}
                onChange={(e) => setNewSlot({ ...newSlot, capacity_total: parseInt(e.target.value) })}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={newSlot.active}
                onCheckedChange={(checked) => setNewSlot({ ...newSlot, active: checked })}
                id="active"
              />
              <Label htmlFor="active">Aktiv</Label>
            </div>
            
            <Button 
              onClick={handleCreateSlot} 
              className="w-full"
              disabled={!newSlot.date}
            >
              Erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
